import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, Switch, Typography, message } from 'antd';
import { CloudServerOutlined, SaveOutlined } from '@ant-design/icons';
import { API_ENDPOINTS } from '../../api/config';
import { get, put } from '../../api/request';

type ContentStudioConfig = {
  enabled: boolean;
  platform_code: string;
  base_url: string;
  recharge_url: string;
  sync_secret: string;
  last_sync_status?: string;
};

const CONTENT_STUDIO_API_URL = 'https://content-studio-h5.cestalt.com';
const CONTENT_STUDIO_ADMIN_URL = 'https://content-studio-ht.cestalt.com/';
const CONTENT_STUDIO_H5_URL = 'https://content-studio-h5.cestalt.com/';
const CONTENT_STUDIO_RECHARGE_URL = `${CONTENT_STUDIO_H5_URL}?page=profile&recharge=1&platform=ai_novel`;

const ContentStudioConfigPage: React.FC = () => {
  const [form] = Form.useForm<ContentStudioConfig>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await get<ContentStudioConfig>(API_ENDPOINTS.contentStudio.config);
        form.setFieldsValue(data);
      } catch (error) {
        message.error(error instanceof Error ? error.message : '加载内容中台配置失败');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [form]);

  const onFinish = async (values: ContentStudioConfig) => {
    setSaving(true);
    try {
      const saved = await put<ContentStudioConfig>(API_ENDPOINTS.contentStudio.config, values);
      form.setFieldsValue(saved);
      message.success('内容中台配置已保存');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存内容中台配置失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        loading={loading}
        title={
          <span>
            <CloudServerOutlined /> 内容中台配置
          </span>
        }
        extra={
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
            保存配置
          </Button>
        }
      >
        <Typography.Paragraph type="secondary">
          子系统主动同步账号、资产、作品热度时会读取这里的内容中台 API 地址和共享密钥。
          用户点击充值时会跳转到配置的内容中台充值页面。
          后台：{CONTENT_STUDIO_ADMIN_URL}；H5：{CONTENT_STUDIO_H5_URL}
        </Typography.Paragraph>
        <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ enabled: true, platform_code: 'ai_novel', base_url: CONTENT_STUDIO_API_URL, recharge_url: CONTENT_STUDIO_RECHARGE_URL }}>
          <Form.Item name="enabled" label="启用同步" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item name="platform_code" label="平台编码" rules={[{ required: true, message: '请输入平台编码' }]}>
            <Input placeholder="ai_novel" />
          </Form.Item>
          <Form.Item name="base_url" label="内容中台 API Base URL" rules={[{ required: true, message: '请输入内容中台 API 地址' }]}>
            <Input placeholder={CONTENT_STUDIO_API_URL} />
          </Form.Item>
          <Form.Item name="recharge_url" label="内容中台充值页面 URL" rules={[{ required: true, message: '请输入内容中台充值页面地址' }]}>
            <Input placeholder={CONTENT_STUDIO_RECHARGE_URL} />
          </Form.Item>
          <Form.Item name="sync_secret" label="共享密钥">
            <Input.Password placeholder="与内容中台 CONTENT_STUDIO_SYNC_SECRET 保持一致" />
          </Form.Item>
          <Form.Item name="last_sync_status" label="状态">
            <Input disabled placeholder="暂无同步状态" />
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ContentStudioConfigPage;
